import type {
  ImageFit,
  ReaderSettings,
  ReadingDirection,
  ReadingMode,
  TitleReadingMode,
} from "~/src/api";

export const enum AvailablePresets {
  SINGLE = "single",
  DOUBLE = "double",
  LONGSTRIP = "longstrip",
}

export type PartialReaderSettings = Partial<ReaderSettings[number]>;

export const PresetMap = new Map<AvailablePresets, PartialReaderSettings>();
PresetMap.set(AvailablePresets.SINGLE, {
  scrollDirection: "horizontal",
  readingMode: "single",
  continuous: false,
  animation: true,
});
PresetMap.set(AvailablePresets.DOUBLE, {
  scrollDirection: "horizontal",
  readingMode: "double",
  continuous: false,
  animation: true,
});
PresetMap.set(AvailablePresets.LONGSTRIP, {
  scrollDirection: "vertical",
  readingMode: "single",
  continuous: true,
  // imageFit: {
  //   current: "fit"
  // }
});

export const getDefaultReaderSettings = (): PartialReaderSettings => ({
  readingMode: "single",
  readingDirection: "content",
  scrollDirection: "horizontal",
  imageFit: { custom: { maximumWidth: 0.75 }, current: "custom" },
  scrollWheelBehavior: "pageTurn",
  continuous: false,
  upscaleSmallPages: true,
  animation: true,
  hideReadingHint: false,
});

export interface ReaderSettingsInterface {
  currentProfileIndex: number;

  // reader settings
  readingMode: ReadingMode;
  readingDirection: ReadingDirection;
  scrollDirection: "horizontal" | "vertical";
  imageFit: { custom: { maximumWidth: number }; current: ImageFit };
  scrollWheelBehavior: "pageTurn" | "zoom" | "none";
  continuous: boolean;
  upscaleSmallPages: boolean;
  animation: boolean;
  hideReadingHint: boolean;

  // unsaved + states
  titleReadingMode?: TitleReadingMode; // only synced reader settings uses this
  _immersionBreak: boolean;
  _isZoomed: boolean;
  menuOpen: boolean;
  commentsOpen: boolean;
  lastOpenedDrawer: "menu" | "comments" | null;
  currentProfile: null | ReaderSettings[number];

  // required functions
  initialize(): Promise<void>;

  toggleMenu(): void;

  toggleComments(): void;

  // only required to set lastOpenedDrawer
  openMenu(): void;

  openComments(): void;

  // reader profile listing function
  getAvailableProfiles(ctx?: {
    "title.readingMode": TitleReadingMode;
    "screen.width": number;
  }): ReaderSettings;

  // reader profile switching function
  applyProfile(
    profileName: string,
    ctx?: {
      "title.readingMode": TitleReadingMode;
      "screen.width": number;
    },
  ): void;

  // for resetting
  resetProfile(profileName: string): void;

  // for highlighting the quick switcher
  getMatchingPresets(): AvailablePresets[];

  // for applying a preset from the quick switcher
  applyPreset(preset: AvailablePresets): void;

  // for compat between synced and unsynced
  onSettingsUpdate(hook: () => void): void;
}
